import React from "react";
import { Box, Typography } from "@mui/material";
import { getBucketScoreColors, scoreToBarPosition, DIVERGING_GRADIENT } from "./sentimentScale";

function formatScore(score) {
  if (score === null || score === undefined || Number.isNaN(score)) return "—";
  return score > 0 ? `+${score.toFixed(1)}` : score.toFixed(1);
}

function ScaleBar({ score }) {
  return (
    <Box sx={{ position: "relative", height: 10, borderRadius: "5px", mt: 0.5, background: DIVERGING_GRADIENT }}>
      <Box
        sx={{
          position: "absolute",
          top: -4,
          left: `${scoreToBarPosition(score)}%`,
          width: 2,
          height: 18,
          bgcolor: "#1a2027",
          borderRadius: "1px",
        }}
      />
    </Box>
  );
}

function TopicScoreRow({ label, score, bg, fg }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1a2027" }}>{label}</Typography>
      <Box sx={{ px: 1, py: 0.3, borderRadius: "12px", fontSize: 12, fontWeight: 700, bgcolor: bg, color: fg, flexShrink: 0 }}>
        {formatScore(score)}
      </Box>
    </Box>
  );
}

function TopicListTile({ label, topics, bg, fg, borderRight }) {
  return (
    <Box sx={{ p: "22px 24px", display: "flex", flexDirection: "column", gap: 1.1, borderRight: borderRight ? "1px solid #eef1f4" : "none" }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#98a2b3" }}>
        {label}
      </Typography>
      {topics && topics.length > 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {topics.map((t) => (
            <TopicScoreRow key={t.label} label={t.label} score={t.score} bg={bg} fg={fg} />
          ))}
        </Box>
      ) : (
        <Typography sx={{ fontSize: 14, color: "#98a2b3" }}>—</Typography>
      )}
    </Box>
  );
}

/**
 * Top summary bar: overall run sentiment + most positive / most negative
 * topic tiles (listing every topic tied at that score, not just one).
 */
export default function SummaryBar({ overall, mostPositive, mostNegative }) {
  const overallColors = getBucketScoreColors(overall);
  const lean = overall === null || overall === undefined ? "No data" : overall < -0.3 ? "Negative lean" : overall > 0.3 ? "Positive lean" : "Neutral";

  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: "8px", boxShadow: "0px 3.5px 5.5px 0px rgba(0,0,0,0.05)", display: "grid", gridTemplateColumns: "340px 1fr" }}>
      <Box sx={{ p: "22px 26px", borderRight: "1px solid #eef1f4", display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#98a2b3" }}>
          Net Brand Sentiment
        </Typography>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.25 }}>
          <Typography sx={{ fontSize: 44, fontWeight: 700, color: overallColors.fg, lineHeight: 1 }}>{formatScore(overall)}</Typography>
          <Box sx={{ px: 1.1, py: 0.4, borderRadius: "12px", fontSize: 12.5, fontWeight: 700, bgcolor: overallColors.bg, color: overallColors.fg }}>
            {lean}
          </Box>
        </Box>
        <ScaleBar score={overall} />
        <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, color: "#98a2b3" }}>
          <span>&minus;3</span>
          <span>0</span>
          <span>+3</span>
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)" }}>
        <TopicListTile label="Most Positive Topic(s)" topics={mostPositive} bg="#eaf6ec" fg="#1b5e20" borderRight />
        <TopicListTile label="Most Negative Topic(s)" topics={mostNegative} bg="#fdecea" fg="#c0362c" />
      </Box>
    </Box>
  );
}
